import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { DiktatAktion, DiktatAktionModul, diktatAktionBestaetigen, diktatAktionVerwerfen } from '../../lib/diktat-api';
import { useSchueler } from '../../contexts/SchuelerContext';
import { useKlassen } from '../../contexts/KlassenContext';
import { apiGet, ApiError } from '../../lib/api';
import { GespraechSheet } from '../../features/schueler/profil/GespraechSheet';
import type { ElternKontakt, Gespraech } from '../../features/schueler/profil/profil-api';
import { useIsMobile } from '../../lib/hooks';
import { MIcon } from '../../lib/micon';
import { LdFeld, LdFeldReihe, LdEingabe, LdTextfeld, LdAuswahl, LdDatum, LdUhrzeit, LdZahl, LdUmschalter, LdChips } from '../ld/LdFenster';
import { LdGradePicker } from '../ld/LdForm';
import { AttestFrage } from '../../features/fehlzeiten/AttestFrage';
import './diktat-review.css';

// ── Hilfsfunktionen ─────────────────────────────────────────────────────────

function normName(s: string) { return s.toLowerCase().trim().replace(/\s+/g, ' '); }

function matchSchueler(name: string, schuelerListe: { id: number; name: string }[]) {
  if (!name) return null;
  const t = normName(name);
  const exact = schuelerListe.find(s => normName(s.name) === t);
  if (exact) return exact;
  const parts = t.split(' ').filter(p => p.length > 2);
  return schuelerListe.find(s => parts.some(p => normName(s.name).includes(p))) ?? null;
}

const MODUL_ICON: Record<DiktatAktionModul, string> = {
  fehlzeit:   'event_busy',
  mehrarbeit: 'more_time',
  notiz:      'sticky_note_2',
  termin:     'event',
  task:       'task_alt',
  mitarbeit:  'back_hand',   // Welle B3
  note:       'grading',
  gespraech:  'forum',
};
const MODUL_LABEL: Record<DiktatAktionModul, string> = {
  fehlzeit:   'Fehlzeit',
  mehrarbeit: 'Mehrarbeit',
  notiz:      'Notiz',
  termin:     'Termin',
  task:       'Aufgabe',
  mitarbeit:  'Mitarbeit',
  note:       'Note',
  gespraech:  'Gespräch',
};
const NOTEN_ARTEN: [string, string][] = [['KA', 'Klassenarbeit'], ['Test', 'Test'], ['Muendlich', 'Mündlich'], ['Sonstiges', 'Sonstiges']];
const MEHRARBEIT_TYPEN: [string, string][] = [['vertretung', 'Vertretung'], ['aufsicht', 'Aufsicht'], ['mehrarbeit', 'Mehrarbeit'], ['sonstiges', 'Sonstiges']];
const NOTIZ_KAT: [string, string][] = [['Positiv', 'Positiv'], ['Negativ', 'Negativ'], ['Fehlzeit', 'Fehlzeit'], ['HA_vergessen', 'HA vergessen'], ['Sonstiges', 'Sonstiges']];
const TERMIN_KAT: [string, string][] = [['eltern', 'Elterngespräch'], ['konferenz', 'Konferenz'], ['klassenfahrt', 'Klassenfahrt'], ['zeugnis', 'Zeugniskonferenz'], ['ferien', 'Ferien'], ['ausflug', 'Ausflug'], ['sonstiges', 'Sonstiges']];

const ttmm = (d: unknown) => { const s = String(d ?? ''); return /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.` : ''; };
const zahl = (n: unknown) => String(n).replace('.', ',');
const label = (paare: [string, string][], k: unknown) => paare.find(([x]) => x === k)?.[1] ?? String(k ?? '');

/** Steuerung für „Alle übernehmen“: true = übernommen, false = braucht eine Entscheidung (bleibt offen) */
type Uebernehmen = () => Promise<boolean>;

// ── Sub-Komponente pro Karte ────────────────────────────────────────────────

function AktionsKarte({
  aktion,
  onErledigt,
  registriere,
}: {
  aktion: DiktatAktion;
  onErledigt: (status: 'bestaetigt' | 'verworfen') => void;
  registriere: (id: number, fn: Uebernehmen | null) => void;
}) {
  const { schuelerListe } = useSchueler();

  // Rohdaten aus Claude parsen
  const rawDaten: Record<string, unknown> = (() => {
    try { return JSON.parse(aktion.daten_json) as Record<string, unknown>; }
    catch { return {}; }
  })();

  // Schüler-Vorauswahl
  // Welle P1b: im Profil diktiert → der Server hat schueler_id schon gesetzt
  const voraugewaehlterSchueler = (typeof rawDaten.schueler_id === 'number'
    ? schuelerListe.find(x => x.id === rawDaten.schueler_id) : null) ?? matchSchueler(
    String(rawDaten.schueler_name ?? ''),
    schuelerListe,
  );

  // Lokaler Form-State
  const heute = new Date().toISOString().split('T')[0];
  const [daten, setDaten] = useState<Record<string, unknown>>(() => {
    const base: Record<string, unknown> = { ...rawDaten };
    if (voraugewaehlterSchueler) base.schueler_id = voraugewaehlterSchueler.id;
    if (!base.datum) base.datum = heute;
    if (aktion.modul === 'fehlzeit') {
      if (base.tage === undefined) base.tage = 1;
      if (base.entschuldigt === undefined) base.entschuldigt = false;
    }
    if (aktion.modul === 'mehrarbeit') {
      if (!base.typ)    base.typ = 'mehrarbeit';
      if (!base.stunden) base.stunden = 1;
    }
    if (aktion.modul === 'termin') {
      const VALID_KAT = ['konferenz','eltern','klassenfahrt','zeugnis','ferien','sonstiges','ausflug'];
      const KAT_MAP: Record<string, string> = {
        'elterngespraech': 'eltern', 'elterngespräch': 'eltern',
        'elternsprechtag': 'eltern', 'elternabend': 'eltern',
        'zeugniskonferenz': 'zeugnis', 'lehrerkonferenz': 'konferenz',
        'klassenkonferenz': 'konferenz', 'schulausflug': 'ausflug',
      };
      const roh = String(base.kategorie ?? '').toLowerCase();
      base.kategorie = KAT_MAP[roh] ?? (VALID_KAT.includes(roh) ? roh : 'sonstiges');
    }
    return base;
  });
  const [laden,  setLaden]  = useState(false);
  const [fehler, setFehler] = useState('');
  const [erledigt, setErledigt] = useState<'bestaetigt' | 'verworfen' | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);

  // ── Welle B3: Mitarbeit, Note, Gespräch ──
  const { meineKlassen } = useKlassen();
  const isMobile = useIsMobile();
  const [als, setAls] = useState<'mitarbeit' | null>(null);         // „Als Mitarbeit werten“ (Notiz)
  const [konflikt, setKonflikt] = useState<number | null>(null);      // Wert dieser Woche schon da
  const [gespraechEltern, setGespraechEltern] = useState<ElternKontakt[] | null>(null);
  const modul: DiktatAktionModul = als ?? aktion.modul;
  const kind = schuelerListe.find(x => x.id === Number(daten.schueler_id)) ?? null;
  const faecherKind = kind ? Array.from(new Set(meineKlassen.filter(k => Number(k.klasse_id) === Number(kind.klasse_id)).map(k => k.fach.trim()))) : [];
  // Fach nur übernehmen, wenn es eins meiner Fächer der Klasse ist; bei genau einem vorbelegen
  useEffect(() => {
    if (!['mitarbeit', 'note'].includes(modul) || !kind) return;
    const f = String(daten.fach ?? '').trim().toLowerCase();
    const passend = faecherKind.find(x => x.toLowerCase() === f) ?? (faecherKind.length === 1 ? faecherKind[0] : '');
    if (passend !== daten.fach) setDaten(d => ({ ...d, fach: passend }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modul, kind?.id, faecherKind.join('|')]);

  // „Alle übernehmen“: nur Karten ohne offene Entscheidung (Gespräch, Ersetzen?, fehlender Wert bleiben stehen)
  const uebernehmenRef = useRef<Uebernehmen>(async () => false);
  uebernehmenRef.current = async () => {
    if (erledigt || laden || modul === 'gespraech' || konflikt !== null) return false;
    return bestaetigen();
  };
  useEffect(() => {
    registriere(aktion.id, () => uebernehmenRef.current());
    return () => registriere(aktion.id, null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktion.id]);

  if (erledigt) {
    return (
      <div className={`ld-dr-karte ist-erledigt${erledigt === 'verworfen' ? ' ist-verworfen' : ''}`}>
        <span className="ld-dr-karte__icon" aria-hidden="true"><MIcon name={erledigt === 'bestaetigt' ? 'check' : 'close'} size={18} /></span>
        <div className="ld-dr-karte__text">
          <small>{MODUL_LABEL[modul]}</small>
          <b>{erledigt === 'bestaetigt' ? 'Übernommen' : 'Verworfen'}</b>
        </div>
      </div>
    );
  }

  function set(key: string, val: unknown) {
    setDaten(d => ({ ...d, [key]: val }));
  }

  async function bestaetigen(ersetzen = false): Promise<boolean> {
    setFehler(''); setKonflikt(null);
    if ((modul === 'mitarbeit' || modul === 'note') && !daten.fach) { setFehler('Bitte ein Fach wählen.'); setBearbeiten(true); return false; }
    if (modul === 'mitarbeit' && !daten.wert) { setFehler('Bitte einen Wert von 1 bis 6 antippen.'); setBearbeiten(true); return false; }
    if (modul === 'note' && !daten.note) { setFehler('Bitte eine Note von 1 bis 6 antippen.'); setBearbeiten(true); return false; }
    setLaden(true);
    try {
      // Welle B4: das Attest gilt nur für ein Kind mit Attestpflicht (Kind kann nach der Antwort gewechselt worden sein)
      const mitPflicht = modul === 'fehlzeit' && schuelerListe.find(x => x.id === Number(daten.schueler_id))?.attestpflicht === 1;
      const senden = modul === 'fehlzeit' ? { ...daten, attest: mitPflicht && !!daten.attest } : daten;
      await diktatAktionBestaetigen(aktion.id, senden, { ...(als ? { als } : {}), ...(ersetzen ? { ersetzen: true } : {}) });
      setErledigt('bestaetigt');
      onErledigt('bestaetigt');
      return true;
    } catch (e: any) {
      // Welle B3: schon ein Wert in dieser Woche → in der Karte fragen (wie „ersetzen“ im Profil)
      if (e instanceof ApiError && e.status === 409) setKonflikt(Number((e.body as any)?.vorhanden?.[0]?.note) || 0);
      else setFehler(e.message ?? 'Fehler');
      return false;
    } finally {
      setLaden(false);
    }
  }

  // Gespräch: Gesprächsfenster mit den erkannten Feldern (vorhandene Strukturierung), bestätigt beim Speichern
  async function gespraechOeffnen() {
    if (!kind) { setFehler('Bitte ein Kind wählen.'); setBearbeiten(true); return; }
    setFehler(''); setLaden(true);
    try { setGespraechEltern((await apiGet<{ eltern: ElternKontakt[] }>(`/api/schueler/${kind.id}/ueberblick`)).eltern ?? []); }
    catch (e: any) { setFehler(e.message ?? 'Fehler'); }
    finally { setLaden(false); }
  }
  async function gespraechGespeichert(g?: Gespraech) {
    if (!g || !kind) return;
    try {
      await diktatAktionBestaetigen(aktion.id, { schueler_id: kind.id, gespraech_id: g.id });
      setErledigt('bestaetigt'); onErledigt('bestaetigt');
    } catch (e: any) { setFehler(e.message ?? 'Fehler'); }
  }

  async function verwerfen() {
    setFehler(''); setLaden(true);
    try {
      await diktatAktionVerwerfen(aktion.id);
      setErledigt('verworfen');
      onErledigt('verworfen');
    } catch (e: any) {
      setFehler(e.message ?? 'Fehler');
    } finally {
      setLaden(false);
    }
  }

  // ── Kurzfassung: Kind oder Titel fett, eine Detailzeile ──
  const kindName = kind ? kind.name : String(rawDaten.schueler_name ?? '') || 'Kind wählen';
  const { titel, detail } = ((): { titel: string; detail: string } => {
    switch (modul) {
      case 'fehlzeit': return { titel: kindName, detail: [ttmm(daten.datum), daten.stunden ? `${daten.stunden} Std.` : `${daten.tage ?? 1} ${Number(daten.tage ?? 1) === 1 ? 'Tag' : 'Tage'}`, daten.entschuldigt ? 'entschuldigt' : 'unentschuldigt'].filter(Boolean).join(', ') };
      case 'mitarbeit': return { titel: kindName, detail: [daten.fach || 'Fach wählen', `Woche vom ${ttmm(daten.datum)}`, daten.wert ? `Wert ${daten.wert}` : 'Wert antippen'].join(', ') };
      case 'note': return { titel: kindName, detail: [daten.fach || 'Fach wählen', label(NOTEN_ARTEN, daten.art ?? 'Sonstiges'), daten.note ? `Note ${daten.note}` : 'Note antippen'].join(', ') };
      case 'notiz': return { titel: kindName, detail: String(daten.zusammenfassung ?? daten.text ?? '') };
      case 'gespraech': return { titel: kindName, detail: 'Im Gesprächsfenster prüfen und speichern' };
      case 'termin': return { titel: String(daten.titel ?? '') || 'Termin', detail: [ttmm(daten.datum), daten.uhrzeit ? `${daten.uhrzeit} Uhr` : 'ganztägig', label(TERMIN_KAT, daten.kategorie)].join(', ') };
      case 'task': return { titel: String(daten.titel ?? '') || 'Aufgabe', detail: daten.faellig_am ? `fällig am ${ttmm(daten.faellig_am)}` : 'ohne Fälligkeit' };
      case 'mehrarbeit': return { titel: label(MEHRARBEIT_TYPEN, daten.typ), detail: [ttmm(daten.datum), `${zahl(daten.stunden ?? 1)} Std.`, daten.klasse ? String(daten.klasse) : '', daten.beschreibung ? String(daten.beschreibung) : ''].filter(Boolean).join(', ') };
      default: return { titel: MODUL_LABEL[modul], detail: '' };
    }
  })();

  const kindWahl = (
    <LdFeld label="Schüler/in">
      <LdAuswahl aria-label="Schüler/in" value={String(daten.schueler_id ?? '')} placeholder="Schüler/in wählen" onChange={e => set('schueler_id', parseInt(e.target.value) || '')}>
        {schuelerListe.map(s => <option key={s.id} value={s.id}>{s.name}, {s.klasse}</option>)}
      </LdAuswahl>
    </LdFeld>
  );
  const datumFeld = (lbl: string, key = 'datum', optional = false) => (
    <LdFeld label={lbl} optional={optional}>
      <LdDatum aria-label={lbl} required={!optional} value={String(daten[key] ?? (optional ? '' : heute))} onChange={e => set(key, e.target.value)} />
    </LdFeld>
  );

  return (
    <div className="ld-dr-karte">
      <div className="ld-dr-karte__kopf">
        <span className="ld-dr-karte__icon" aria-hidden="true"><MIcon name={MODUL_ICON[modul]} size={19} /></span>
        <div className="ld-dr-karte__text">
          <small>{MODUL_LABEL[modul]}</small>
          <b>{titel}</b>
          {detail && <span>{detail}</span>}
        </div>
        <button type="button" className="ld-dr-karte__weg" onClick={verwerfen} disabled={laden} aria-label={`${MODUL_LABEL[modul]} verwerfen`}>
          <MIcon name="close" size={18} />
        </button>
      </div>

      {/* Welle B4: Attest gleich mitfragen — sichtbar auch ohne „Bearbeiten“, nur bei aktiver Attestpflicht */}
      {modul === 'fehlzeit' && schuelerListe.find(x => x.id === Number(daten.schueler_id))?.attestpflicht === 1 && (
        <div className="ld-dr-karte__attest">
          <AttestFrage attest={!!daten.attest} onChange={ja => { set('attest', ja); if (ja) set('entschuldigt', true); }} />
        </div>
      )}

      {bearbeiten && (
        <div className="ld-dr-karte__felder">
          {modul === 'fehlzeit' && (
            <>
              {kindWahl}
              {datumFeld('Datum')}
              <LdFeldReihe>
                <LdFeld label="Stunden" optional>
                  <LdZahl aria-label="Stunden" value={Number(daten.stunden ?? 0)} min={0} max={10} onChange={n => set('stunden', n || null)} />
                </LdFeld>
                <LdFeld label="Tage">
                  <LdZahl aria-label="Tage" value={Number(daten.tage ?? 1)} min={1} max={30} onChange={n => set('tage', n)} />
                </LdFeld>
              </LdFeldReihe>
              <LdUmschalter aria-label="Status" wert={daten.entschuldigt ? 'ja' : 'nein'} onChange={w => set('entschuldigt', w === 'ja')}
                optionen={[{ wert: 'nein', label: 'Unentschuldigt', ton: 'amber' }, { wert: 'ja', label: 'Entschuldigt', ton: 'gruen' }]} />
            </>
          )}

          {modul === 'mehrarbeit' && (
            <>
              <LdFeld label="Art">
                <LdChips aria-label="Art" wert={String(daten.typ ?? 'mehrarbeit')} onChange={v => set('typ', v)} optionen={MEHRARBEIT_TYPEN.map(([wert, l]) => ({ wert, label: l }))} />
              </LdFeld>
              <LdFeldReihe>
                {datumFeld('Datum')}
                <LdFeld label="Stunden">
                  <LdZahl aria-label="Stunden" value={Number(daten.stunden ?? 1)} min={0.5} max={12} schritt={0.5} onChange={n => set('stunden', n)} />
                </LdFeld>
              </LdFeldReihe>
              <LdFeldReihe>
                <LdFeld label="Klasse" optional>
                  <LdEingabe aria-label="Klasse" value={String(daten.klasse ?? '')} onChange={e => set('klasse', e.target.value)} placeholder="z. B. 8b" />
                </LdFeld>
                <LdFeld label="Beschreibung" optional>
                  <LdEingabe aria-label="Beschreibung" value={String(daten.beschreibung ?? '')} onChange={e => set('beschreibung', e.target.value)} placeholder="z. B. Vertretung Mathe" />
                </LdFeld>
              </LdFeldReihe>
            </>
          )}

          {modul === 'notiz' && (
            <>
              {kindWahl}
              {datumFeld('Datum')}
              <LdFeld label="Kategorie">
                <LdChips aria-label="Kategorie" wert={String(daten.kategorie ?? 'Sonstiges')} onChange={v => set('kategorie', v)} optionen={NOTIZ_KAT.map(([wert, l]) => ({ wert, label: l }))} />
              </LdFeld>
              <LdFeld label="Notiz">
                <LdTextfeld aria-label="Notiz" rows={3} value={String(daten.zusammenfassung ?? daten.text ?? '')} onChange={e => set('zusammenfassung', e.target.value)} placeholder="Beobachtung, Anmerkung" />
              </LdFeld>
            </>
          )}

          {/* ── Welle B3: Mitarbeit (Woche) und Note — Wert nur, wenn gesagt oder angetippt ── */}
          {(modul === 'mitarbeit' || modul === 'note') && (
            <>
              {kindWahl}
              <LdFeld label="Fach">
                {faecherKind.length > 0
                  ? <LdChips aria-label="Fach" wert={String(daten.fach ?? '')} onChange={v => set('fach', v)} optionen={faecherKind.map(f => ({ wert: f, label: f }))} />
                  : <p className="ld-fe-text">{kind ? 'Kein eigenes Fach in dieser Klasse.' : 'Erst ein Kind wählen.'}</p>}
              </LdFeld>
              {datumFeld(modul === 'mitarbeit' ? 'Woche (ein Tag darin)' : 'Datum')}
              {modul === 'note' && (
                <LdFeld label="Art">
                  <LdChips aria-label="Art" wert={String(daten.art ?? 'Sonstiges')} onChange={v => set('art', v)} optionen={NOTEN_ARTEN.map(([wert, l]) => ({ wert, label: l }))} />
                </LdFeld>
              )}
              <LdFeld label={modul === 'mitarbeit' ? 'Wert' : 'Note'}>
                <LdGradePicker value={Number(modul === 'mitarbeit' ? daten.wert : daten.note) || 0} onChange={(w: number) => set(modul === 'mitarbeit' ? 'wert' : 'note', w)} />
              </LdFeld>
              {modul === 'mitarbeit' && (
                <LdFeld label="Bemerkung" optional>
                  <LdEingabe aria-label="Bemerkung" value={String(daten.bemerkung ?? '')} onChange={e => set('bemerkung', e.target.value)} placeholder="z. B. gute Beiträge" />
                </LdFeld>
              )}
            </>
          )}

          {modul === 'gespraech' && kindWahl}

          {modul === 'termin' && (
            <>
              <LdFeld label="Titel">
                <LdEingabe aria-label="Titel" value={String(daten.titel ?? '')} onChange={e => set('titel', e.target.value)} placeholder="z. B. Elterngespräch Müller" />
              </LdFeld>
              <LdFeldReihe>
                {datumFeld('Datum')}
                <LdFeld label="Uhrzeit" optional>
                  <LdUhrzeit aria-label="Uhrzeit" value={String(daten.uhrzeit ?? '')} onChange={z => set('uhrzeit', z)} />
                </LdFeld>
              </LdFeldReihe>
              <LdFeld label="Kategorie">
                <LdChips aria-label="Kategorie" wert={String(daten.kategorie ?? 'sonstiges')} onChange={v => set('kategorie', v)} optionen={TERMIN_KAT.map(([wert, l]) => ({ wert, label: l }))} />
              </LdFeld>
            </>
          )}

          {modul === 'task' && (
            <>
              <LdFeld label="Aufgabe">
                <LdEingabe aria-label="Aufgabe" value={String(daten.titel ?? '')} onChange={e => set('titel', e.target.value)} placeholder="z. B. Aufsätze korrigieren" />
              </LdFeld>
              {datumFeld('Fällig am', 'faellig_am', true)}
            </>
          )}
        </div>
      )}

      {konflikt !== null && (
        <div role="alert" className="ld-dr-karte__frage">
          <span>In dieser Woche ist schon {konflikt ? `der Wert ${konflikt}` : 'ein Wert'} eingetragen. Ersetzen?</span>
          <button type="button" className="ld-dr-knopf ld-dr-knopf--haupt" onClick={() => bestaetigen(true)} disabled={laden}>Ersetzen</button>
        </div>
      )}
      {fehler && <p className="ld-fe-fehler" role="alert">{fehler}</p>}

      {modul === 'gespraech' && kind && (
        <GespraechSheet open={!!gespraechEltern} schuelerId={kind.id} schuelerName={kind.name} vorname={kind.name.split(' ')[0]}
          eltern={gespraechEltern ?? []} gespraech={null} isMobile={isMobile}
          startText={String(daten.text ?? aktion.text_quelle ?? '')}
          onClose={() => setGespraechEltern(null)} onGespeichert={gespraechGespeichert} />
      )}

      {konflikt === null && (
        <div className="ld-dr-karte__aktionen">
          <button type="button" className="ld-dr-knopf ld-dr-knopf--haupt" disabled={laden}
            onClick={() => (modul === 'gespraech' ? gespraechOeffnen() : bestaetigen())}>
            {laden ? 'Bitte warten …' : modul === 'gespraech' ? 'Gespräch öffnen' : 'Übernehmen'}
          </button>
          <button type="button" className="ld-dr-knopf" onClick={() => setBearbeiten(b => !b)} aria-expanded={bearbeiten}>
            {bearbeiten ? 'Fertig' : 'Bearbeiten'}
          </button>
          {/* Welle B3: aus der Beobachtung eine Mitarbeit machen — den Wert wählt die Lehrkraft */}
          {modul === 'notiz' && (
            <button type="button" className="ld-kl-link ld-dr-karte__als"
              onClick={() => { setAls('mitarbeit'); setBearbeiten(true); setDaten(d => ({ ...d, wert: null, bemerkung: String(d.zusammenfassung ?? d.text ?? '') })); }}>
              Als Mitarbeit werten
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── DiktatReview Haupt-Komponente ───────────────────────────────────────────

export interface DiktatReviewSteuerung {
  /** übernimmt alle Karten ohne offene Entscheidung; Rückgabe: übernommen, offen geblieben */
  alleUebernehmen: () => Promise<{ uebernommen: number; offen: number }>;
  /** noch nicht entschiedene Vorschläge und Zahl der übernommenen */
  stand: () => { offeneIds: number[]; bestaetigt: number };
}

export const DiktatReview = React.forwardRef<DiktatReviewSteuerung, {
  transcript: string | null;
  aktionen: DiktatAktion[];
  onComplete: (bestaetigt: number, verworfen: number) => void;
  onOffen?: (offen: number) => void;
  onNeuAufnehmen?: () => void;
}>(function DiktatReview({ transcript, aktionen: initialAktionen, onComplete, onOffen, onNeuAufnehmen }, ref) {
  const [aktionen]   = useState(initialAktionen);
  const [erledigtMap, setErledigtMap] = useState<Record<number, 'bestaetigt' | 'verworfen'>>({});
  const uebernehmer = useRef(new Map<number, Uebernehmen>());

  const offene      = aktionen.filter(a => !erledigtMap[a.id]);
  const bestaetigt  = Object.values(erledigtMap).filter(v => v === 'bestaetigt').length;
  const verworfen   = Object.values(erledigtMap).filter(v => v === 'verworfen').length;

  useEffect(() => { onOffen?.(offene.length); }, [offene.length, onOffen]);

  useEffect(() => {
    if (aktionen.length > 0 && Object.keys(erledigtMap).length === aktionen.length) {
      // Alle erledigt
      setTimeout(() => onComplete(bestaetigt, verworfen), 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erledigtMap]);

  useImperativeHandle(ref, () => ({
    async alleUebernehmen() {
      let uebernommen = 0, offen = 0;
      for (const a of aktionen) {
        const fn = uebernehmer.current.get(a.id);
        if (!fn) continue;
        if (await fn()) uebernommen++; else offen++;
      }
      return { uebernommen, offen };
    },
    stand: () => ({ offeneIds: offene.map(a => a.id), bestaetigt }),
  }), [aktionen, offene, bestaetigt]);

  function onAktionErledigt(id: number, status: 'bestaetigt' | 'verworfen') {
    setErledigtMap(m => ({ ...m, [id]: status }));
  }
  const registriere = (id: number, fn: Uebernehmen | null) => { if (fn) uebernehmer.current.set(id, fn); else uebernehmer.current.delete(id); };

  return (
    <div className="ld-dr">
      {transcript && (
        <section className="ld-dr-gesagt" aria-label="Du hast gesagt">
          <div className="ld-dr-gesagt__kopf">
            <h3>Du hast gesagt</h3>
            {onNeuAufnehmen && <button type="button" className="ld-kl-link" onClick={onNeuAufnehmen}><MIcon name="mic" size={17} />Neu aufnehmen</button>}
          </div>
          <p>„{transcript}“</p>
        </section>
      )}

      {aktionen.length === 0 ? (
        <div className="ld-dr-leer">
          <b>Keine Vorschläge erkannt</b>
          <span>Sag es noch einmal etwas genauer, zum Beispiel mit Namen und Datum.</span>
        </div>
      ) : (
        <>
          <h3 className="ld-dr-titel">Vorschläge <span>{offene.length}</span></h3>
          <div className="ld-dr-liste">
            {aktionen.map(a => (
              <AktionsKarte key={a.id} aktion={a} registriere={registriere} onErledigt={status => onAktionErledigt(a.id, status)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
});
